import { useEffect, useState } from 'react';
import {
  MachineResponseDto,
  UserMachineDto,
  UserType,
  machineListGetApi,
  userListGetApi,
  userMachineListGetApi,
  userMachinePostApi,
} from '../../../api/apiConfig';
import useFetch from '../../../api/useFetch';
import styled from 'styled-components';
import SquareButton from '../../../component/button/SquareButton';
import { useHeaderButtons } from '../../../component/header/HeaderButtonProvider';
import { ConfirmModal } from '../../../component/common/modal/common/ConfirmModal';

export type UserMachineDetail = {
  user: UserType;
  machine: MachineResponseDto;
};

export const MachineAssignment = () => {
  const { setButtons } = useHeaderButtons();

  const [openDupModal, setOpenDupModal] = useState<boolean>(false);

  const [machineList, setMachineList] = useState<MachineResponseDto[]>([]);
  const [userList, setUserList] = useState<UserType[]>([]);
  const [userLoaded, setUserLoaded] = useState<boolean>(false);
  const [machineLoaded, setMachineLoaded] = useState<boolean>(false);
  const [selectedMachine, setSelectedMachine] =
    useState<MachineResponseDto | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);

  const [userMachineDtos, setUserMachineDtos] = useState<UserMachineDto[]>([]);

  const [userMachineList, setUserMachineList] = useState<UserMachineDetail[]>(
    []
  );

  const { fetch: fetchUserListGetApi } = useFetch({
    fetchFunction: userListGetApi,
    onSuccess: (data: UserType[]) => {
      setUserList(
        data.filter((user) => user.role !== 'ADMIN' && user.role !== 'SUPER')
      );
      setUserLoaded(true);
    },
  });

  const { fetch: fetchMachineListGetApi } = useFetch({
    fetchFunction: machineListGetApi,
    onSuccess: (data: MachineResponseDto[]) => {
      setMachineList(data);
      setMachineLoaded(true);
    },
  });

  const { fetch: fetchUserMachineListGetApi } = useFetch({
    fetchFunction: userMachineListGetApi,
    onSuccess: (data: UserMachineDto[]) => setUserMachineDtos(data),
  });

  const { fetch: fetchUserMachinePostApi } = useFetch({
    fetchFunction: userMachinePostApi,
    onSuccess: (data: UserMachineDto) => {
      fetchUserMachineListGetApi();
    },
  });

  useEffect(() => {
    fetchUserListGetApi();
    fetchMachineListGetApi();
    fetchUserMachineListGetApi();
  }, []);

  function genUserMachineDetail(dto: UserMachineDto): UserMachineDetail | null {
    const tUser = userList.find((user) => user.id === dto.userId) || null;
    const tMachine =
      machineList.find((machine) => machine.id === dto.machineId) || null;

    if (!tUser || !tMachine) return null;

    const newDetail: UserMachineDetail = {
      user: tUser,
      machine: tMachine,
    };
    return newDetail;
  }

  useEffect(() => {
    if (!userLoaded || !machineLoaded || userMachineDtos.length < 1) return;

    const newDetails: UserMachineDetail[] = [];

    userMachineDtos.forEach((userMachine) => {
      const newDetail = genUserMachineDetail(userMachine);
      newDetail && newDetails.push(newDetail);
    });
    setUserMachineList(newDetails);
  }, [userLoaded, machineLoaded, userMachineDtos]);

  function machineClickHandler(machine: MachineResponseDto) {
    if (machine.id === selectedMachine?.id) {
      setSelectedMachine(null);
    } else {
      setSelectedMachine(machine);
    }
  }

  function userClickHandler(user: UserType) {
    if (user.id === selectedUser?.id) {
      setSelectedUser(null);
    } else {
      setSelectedUser(user);
    }

    const tUserMachine =
      userMachineList.find((userMachine) => userMachine.user.id === user.id) ||
      null;
    if (tUserMachine) {
      setSelectedMachine(tUserMachine.machine);
    } else {
      setSelectedMachine(null);
    }
  }

  function saveValid() {
    if (!selectedUser || !selectedMachine || !selectedUser.id) return;
    const eUserMachine =
      userMachineDtos.find(
        (userMachine) =>
          userMachine.machineId === selectedMachine.id &&
          userMachine.userId !== selectedUser.id
      ) || null;
    if (eUserMachine) {
      setOpenDupModal(true);
    } else {
      saveHandler();
    }
  }

  function saveHandler() {
    if (!selectedMachine || !selectedUser || !selectedUser.id) return;
    const dto: UserMachineDto = {
      id: -1,
      machineId: selectedMachine.id,
      userId: selectedUser.id,
    };
    fetchUserMachinePostApi(dto);
    setOpenDupModal(false);
  }

  useEffect(() => {
    //헤더에 버튼 추가
    setButtons(
      <>
        <SquareButton
          text='저장하기'
          onClick={() => saveValid()}
          colorType={
            selectedUser !== null && selectedMachine !== null
              ? 'ACTIVE'
              : 'DISACTIVE'
          }
        />
      </>
    );
    // 페이지가 바뀌면 버튼 초기화
    return () => setButtons(null);
  }, [setButtons, selectedUser, selectedMachine]);

  return (
    <Container>
      {openDupModal && (
        <ConfirmModal
          text={`${selectedMachine?.name}기기 담당유저가 이미 존재합니다.\n해당 유저와의 연결을 해제하고 새로 등록하시겠습니까?`}
          onClose={() => setOpenDupModal(false)}
          onConfirm={() => saveHandler()}
        />
      )}
      <InfoArea>
        <div className='row'>
          <div
            className='label'
            style={{ whiteSpace: 'nowrap', marginBottom: '10px' }}
          >
            ※ 각 유저별로 저장하기를 클릭하여야 정상적으로 저장됩니다
          </div>
        </div>
        <div className='row'>
          <div className='label'>선택된 유저 :</div>
          <div className='value'>{selectedUser?.userName}</div>
        </div>
        <div className='row'>
          <div className='label'>선택된 기계 :</div>
          <div className='value'>{selectedMachine?.name}</div>
        </div>
      </InfoArea>
      <BodyContainer>
        <HalfContainer>
          <HalfHead>유저 목록</HalfHead>
          <ListWrapper>
            {userList &&
              userList.length > 0 &&
              userList.map((user, index) => (
                <>
                  <ListItem
                    $selected={selectedUser?.id === user.id}
                    onClick={() => userClickHandler(user)}
                  >
                    <div className='row'>
                      <div className='label'>유저 이름</div>
                      <div className='value'>{user.userName}</div>
                    </div>
                    <div className='row'>
                      <div className='label'>로그인ID</div>
                      <div className='value'>{user.loginId}</div>
                    </div>
                    <div className='row'>
                      <div className='label'>연동된 기계</div>
                      <div className='value'>
                        {userMachineList.find(
                          (userMachineDetail) =>
                            userMachineDetail.user.id === user.id
                        )?.machine.name || '-'}
                      </div>
                    </div>
                  </ListItem>
                  {index + 1 < userList.length && <Division />}
                </>
              ))}
          </ListWrapper>
        </HalfContainer>
        <HalfContainer>
          <HalfHead>기계 목록</HalfHead>
          <ListWrapper>
            {machineList &&
              machineList.length > 0 &&
              machineList.map((machine, index) => (
                <>
                  <ListItem
                    $selected={selectedMachine?.id === machine.id}
                    onClick={() => machineClickHandler(machine)}
                  >
                    <div className='row'>
                      <div className='label'>기계 이름</div>
                      <div className='value'>{machine.name}</div>
                    </div>
                    <div className='row'>
                      <div className='label'>기계 식별자</div>
                      <div className='value'>
                        {machine.identifier !== null &&
                        machine.identifier?.trim() !== ''
                          ? machine.identifier
                          : '-'}
                      </div>
                    </div>
                    <div className='row'>
                      <div className='label'>작업장 정보</div>
                      <div className='value'>{machine.workbay.workbayName}</div>
                    </div>
                    <div className='row'>
                      <div className='label'>연동된 유저</div>
                      <div className='value'>
                        {userMachineList.find(
                          (userMachineDetail) =>
                            userMachineDetail.machine.id === machine.id
                        )?.user.userName || '-'}
                      </div>
                    </div>
                  </ListItem>
                  {index + 1 < machineList.length && <Division />}
                </>
              ))}
          </ListWrapper>
        </HalfContainer>
      </BodyContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 25px;
  height: 100%;
`;

const InfoArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 10px;
  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
    .label {
      width: 100px;
      font-size: 14px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.charcoal};
    }
    .value {
      font-size: 14px;
      font-weight: 500;
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

const BodyContainer = styled.div`
  * {
    box-sizing: border-box;
  }
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 25px;
`;

const HalfContainer = styled.div`
  flex: 1;
  max-height: 600px;
  padding: 23px;

  background-color: white;
  display: flex;
  flex-direction: column;
`;

const HalfHead = styled.div`
  font-size: 22px;
  color: ${({ theme }) => theme.colors.charcoal};
  font-weight: 600;
  margin-bottom: 25px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const ListWrapper = styled.div`
  flex: 1;
  overflow-y: scroll;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.charcoal};
`;

const ListItem = styled.div<{ $selected: boolean }>`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 10px;
  background-color: ${({ $selected, theme }) =>
    $selected && theme.colors.lightGray};
  .row {
    display: flex;
    flex-direction: row;
    align-items: center;
    .label {
      width: 100px;
      font-size: 14px;
      font-weight: 600;
      color: ${({ theme }) => theme.colors.charcoal};
    }
    .value {
      font-size: 14px;
      font-weight: 500;
      color: ${({ theme }) => theme.colors.black};
    }
  }
`;

const Division = styled.div`
  height: 1px;
  width: 97%;
  align-self: center;
  background-color: ${({ theme }) => theme.colors.charcoal};
`;
